import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { uomService } from 'parts/services';

import { IBulkUpsertUomConversionsForm } from './types';

interface IProps {
  partCount: number;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IBulkUpsertUomConversionsForm>) => void;
  onSubmit: (
    reset?: UseFormReset<IBulkUpsertUomConversionsForm>
  ) => (data: IBulkUpsertUomConversionsForm) => void;
}

// Base UOM is picked from the master list rather than typed: the server rejects a
// from_uom that is not a base unit. Same source the single-part form uses.
const getUomBaseOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
  uomService.getManyUoms({ ...requestPayload, filters: { isBase: 1 } }, signal);

export const BulkUpsertUomConversionsFormFields = ({
  partCount,
  isOpen,
  onClose,
  onSubmit,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  return (
    <Form
      title={t('titleBulkUomConversions')}
      subtitle={t('messageBulkUomConversionsSubtitle', { count: partCount })}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelAddUomConversions')}
    >
      <FormField
        name="baseUom"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          control,
          error: errors?.baseUom,
          label: t('labelBaseUom'),
          required: true,
          disableClearable: true,
          getOptions: getUomBaseOptions,
          optionKey: 'name',
          enabledOnFocus: false,
        }}
      />

      <FormField
        name="alternateUom"
        type={InputType.TEXT_INPUT}
        inputProps={{
          control,
          error: errors?.alternateUom,
          label: t('labelAlternateUom'),
          required: true,
        }}
      />

      <FormField
        name="multiplier"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          control,
          error: errors?.multiplier,
          label: t('labelMultiplier'),
          required: true,
        }}
      />
    </Form>
  );
};
